import * as THREE from 'three';

/**
 * General-purpose particle system for visual effects.
 *
 * Uses object pooling and THREE.InstancedMesh for performance.
 * Maximum 500 active particles at any time.
 * Particles beyond 50 blocks from the player are culled.
 */

const MAX_PARTICLES = 500;
const CULL_DISTANCE = 50;

// Shared geometries (created once)
let _cubeGeo = null;
let _pointGeo = null;

function getCubeGeo() {
  if (!_cubeGeo) _cubeGeo = new THREE.BoxGeometry(0.1, 0.1, 0.1);
  return _cubeGeo;
}

function getPointGeo() {
  if (!_pointGeo) _pointGeo = new THREE.BoxGeometry(0.05, 0.05, 0.05);
  return _pointGeo;
}

// Dummy object for matrix composition
const _dummy = new THREE.Object3D();
const _color = new THREE.Color();

export class ParticleSystem {
  /**
   * @param {THREE.Scene} scene
   */
  constructor(scene) {
    this.scene = scene;

    // ---- Object Pool ----
    // Each particle is a plain object with properties:
    //   position, velocity, color, life, maxLife, size, type, gravity, fadeOut, target
    this._pool = [];
    this._active = [];

    // Pre-allocate pool
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this._pool.push(this._createParticleObj());
    }

    // ---- InstancedMesh for cube particles (block break, sparks, etc.) ----
    const cubeMat = new THREE.MeshBasicMaterial({
      vertexColors: false,
      transparent: true,
      opacity: 1,
      depthWrite: false,
    });
    // We use a single InstancedMesh and update instance colors + matrices each frame.
    this._cubeMesh = new THREE.InstancedMesh(getCubeGeo(), cubeMat, MAX_PARTICLES);
    this._cubeMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // Per-instance colors
    this._cubeMesh.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(MAX_PARTICLES * 3), 3
    );
    this._cubeMesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this._cubeMesh.frustumCulled = false;
    this._cubeMesh.count = 0;
    scene.add(this._cubeMesh);

    // ---- InstancedMesh for point (tiny) particles (sparks, smoke, xp) ----
    const pointMat = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 1,
      depthWrite: false,
    });
    this._pointMesh = new THREE.InstancedMesh(getPointGeo(), pointMat, MAX_PARTICLES);
    this._pointMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this._pointMesh.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(MAX_PARTICLES * 3), 3
    );
    this._pointMesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this._pointMesh.frustumCulled = false;
    this._pointMesh.count = 0;
    scene.add(this._pointMesh);

    // Player position for culling (updated externally or via update)
    this._playerPos = new THREE.Vector3();

    // Timer accumulators for continuous effects
    this._torchTimers = new Map(); // posKey -> timer
    this._smokeTimers = new Map();
  }

  // ------------------------------------------------------------------
  // Public API
  // ------------------------------------------------------------------

  /**
   * Update all active particles.
   * @param {number} dt - delta time in seconds
   * @param {THREE.Vector3} [playerPos] - current player position for culling
   */
  update(dt, playerPos) {
    if (playerPos) this._playerPos.copy(playerPos);

    // Update active particles
    for (let i = this._active.length - 1; i >= 0; i--) {
      const p = this._active[i];
      p.life -= dt;

      // Dead or too far
      if (p.life <= 0 || p.position.distanceTo(this._playerPos) > CULL_DISTANCE) {
        this._recycle(i);
        continue;
      }

      // Physics
      if (p.gravity) {
        p.velocity.y += p.gravity * dt;
      }

      // Magnetic target (XP orbs)
      if (p.target) {
        const dir = new THREE.Vector3().subVectors(p.target, p.position);
        const dist = dir.length();
        if (dist < 0.5) {
          // Collected
          this._recycle(i);
          continue;
        }
        dir.normalize().multiplyScalar(8 * dt); // magnetic pull speed
        p.velocity.add(dir);
        // Dampen velocity to avoid oscillation
        p.velocity.multiplyScalar(0.95);
      }

      p.position.add(p.velocity.clone().multiplyScalar(dt));

      // Fade out
      if (p.fadeOut) {
        p.opacity = THREE.MathUtils.clamp(p.life / (p.maxLife * 0.5), 0, 1);
      }
    }

    // Rebuild instanced mesh data
    this._rebuildInstances();
  }

  /**
   * Spawn block break particles at the given position.
   * @param {THREE.Vector3} position - center of the broken block
   * @param {THREE.Color|number} blockColor - color of the block
   */
  spawnBlockBreak(position, blockColor) {
    const count = 8 + Math.floor(Math.random() * 5); // 8-12
    const color = blockColor instanceof THREE.Color ? blockColor : new THREE.Color(blockColor);

    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return; // pool exhausted

      p.position.copy(position);
      // Random offset within the block
      p.position.x += (Math.random() - 0.5) * 0.8;
      p.position.y += (Math.random() - 0.5) * 0.8;
      p.position.z += (Math.random() - 0.5) * 0.8;

      // Explode outward
      p.velocity.set(
        (Math.random() - 0.5) * 4,
        Math.random() * 3 + 1,
        (Math.random() - 0.5) * 4
      );

      p.color.copy(color);
      // Slight color variation
      p.color.r += (Math.random() - 0.5) * 0.1;
      p.color.g += (Math.random() - 0.5) * 0.1;
      p.color.b += (Math.random() - 0.5) * 0.1;

      p.life = 0.4 + Math.random() * 0.2; // 0.4-0.6s (fast fade)
      p.maxLife = p.life;
      p.size = 0.05;
      p.type = 'cube';
      p.gravity = -15;
      p.fadeOut = true;
      p.opacity = 1;
      p.target = null;
    }
  }

  /**
   * Spawn torch spark particles (call each frame for ongoing effect).
   * Internally manages a timer so it emits 1-2 sparks every 0.3s.
   * @param {THREE.Vector3} position - torch top position
   */
  spawnTorchSparks(position) {
    const key = `${Math.floor(position.x)},${Math.floor(position.y)},${Math.floor(position.z)}`;
    // Sparks are spawned based on timer, not every call
    // Caller should call this every frame; we throttle internally
    if (!this._torchTimers.has(key)) {
      this._torchTimers.set(key, 0);
    }
    // Timer is advanced in spawnTorchSparksUpdate
  }

  /**
   * Update torch spark timers and spawn particles.
   * Call this once per frame with dt.
   * @param {number} dt
   * @param {THREE.Vector3[]} torchPositions - all nearby torch positions
   */
  updateTorchSparks(dt, torchPositions) {
    for (const pos of torchPositions) {
      const key = `${Math.floor(pos.x)},${Math.floor(pos.y)},${Math.floor(pos.z)}`;
      let timer = this._torchTimers.get(key) || 0;
      timer += dt;

      if (timer >= 0.3) {
        timer = 0;
        const count = 1 + Math.floor(Math.random() * 2); // 1-2
        for (let i = 0; i < count; i++) {
          const p = this._acquire();
          if (!p) return;

          p.position.set(
            pos.x + (Math.random() - 0.5) * 0.2,
            pos.y + 0.5,
            pos.z + (Math.random() - 0.5) * 0.2
          );
          p.velocity.set(
            (Math.random() - 0.5) * 0.3,
            0.8 + Math.random() * 0.4, // float up ~1 block/sec
            (Math.random() - 0.5) * 0.3
          );
          // Orange-yellow color
          p.color.setRGB(1.0, 0.6 + Math.random() * 0.3, 0.1);
          p.life = 0.4 + Math.random() * 0.2; // 0.4-0.6s
          p.maxLife = p.life;
          p.size = 0.05;
          p.type = 'point';
          p.gravity = 0;
          p.fadeOut = true;
          p.opacity = 1;
          p.target = null;
        }
      }

      this._torchTimers.set(key, timer);
    }

    // Clean up timers for torches no longer in view
    if (this._torchTimers.size > 200) {
      this._torchTimers.clear();
    }
  }

  /**
   * Spawn melee hit impact particles (blood-free, white/gray sparks).
   * @param {THREE.Vector3} position - hit position (entity center)
   * @param {boolean} [critical=false] - if true, spawn extra particles
   */
  spawnHitImpact(position, critical = false) {
    const count = critical ? 12 : 6;

    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return;

      p.position.copy(position);
      p.position.x += (Math.random() - 0.5) * 0.4;
      p.position.y += (Math.random() - 0.5) * 0.4;
      p.position.z += (Math.random() - 0.5) * 0.4;

      // Burst outward
      const angle = Math.random() * Math.PI * 2;
      const upAngle = Math.random() * Math.PI * 0.6 - 0.1;
      const speed = 2 + Math.random() * 3;
      p.velocity.set(
        Math.cos(angle) * Math.cos(upAngle) * speed,
        Math.sin(upAngle) * speed + 1.5,
        Math.sin(angle) * Math.cos(upAngle) * speed
      );

      // White-gray sparks
      const brightness = 0.7 + Math.random() * 0.3;
      p.color.setRGB(brightness, brightness, brightness);
      p.life = 0.15 + Math.random() * 0.15; // 0.15-0.3s (fast)
      p.maxLife = p.life;
      p.size = 0.06;
      p.type = 'cube';
      p.gravity = -10;
      p.fadeOut = true;
      p.opacity = 1;
      p.target = null;
    }
  }

  /**
   * Spawn critical hit particles (star burst).
   * @param {THREE.Vector3} position - hit position
   */
  spawnCriticalHit(position) {
    const count = 5 + Math.floor(Math.random() * 4); // 5-8

    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return;

      p.position.copy(position);
      p.position.x += (Math.random() - 0.5) * 0.3;
      p.position.y += (Math.random() - 0.5) * 0.3;
      p.position.z += (Math.random() - 0.5) * 0.3;

      // Burst outward
      const angle = Math.random() * Math.PI * 2;
      const upAngle = Math.random() * Math.PI - Math.PI / 2;
      const speed = 2 + Math.random() * 3;
      p.velocity.set(
        Math.cos(angle) * Math.cos(upAngle) * speed,
        Math.sin(upAngle) * speed + 1,
        Math.sin(angle) * Math.cos(upAngle) * speed
      );

      // Yellow-white
      p.color.setRGB(1.0, 0.9 + Math.random() * 0.1, 0.5 + Math.random() * 0.3);
      p.life = 0.2 + Math.random() * 0.15; // 0.2-0.35s
      p.maxLife = p.life;
      p.size = 0.08;
      p.type = 'point';
      p.gravity = -5;
      p.fadeOut = true;
      p.opacity = 1;
      p.target = null;
    }
  }

  /**
   * Spawn XP orb particles that fly toward the player.
   * @param {THREE.Vector3} position - spawn position
   * @param {THREE.Vector3} target - player position (orbs fly toward this)
   * @param {number} [count=3] - number of orbs
   */
  spawnXPOrb(position, target, count = 3) {
    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return;

      p.position.copy(position);
      p.position.x += (Math.random() - 0.5) * 0.5;
      p.position.y += Math.random() * 0.5;
      p.position.z += (Math.random() - 0.5) * 0.5;

      // Initial gentle upward velocity + slight random
      p.velocity.set(
        (Math.random() - 0.5) * 1,
        1 + Math.random(),
        (Math.random() - 0.5) * 1
      );

      // Green glow
      p.color.setRGB(0.2, 0.9 + Math.random() * 0.1, 0.2);
      p.life = 3; // long life (should reach player before this)
      p.maxLife = p.life;
      p.size = 0.1;
      p.type = 'point';
      p.gravity = -2;
      p.fadeOut = false;
      p.opacity = 1;
      p.target = target; // magnetic target
    }
  }

  /**
   * Spawn torch flame particles. Small orange/yellow particles floating upward.
   * Unlike spawnTorchSparks (which is timer-based), this is a one-shot burst.
   * 2-3 particles, small size (0.05), lifetime 0.5-1s, gentle upward drift.
   * @param {THREE.Vector3} position - torch position
   */
  spawnTorchFlame(position) {
    const count = 2 + Math.floor(Math.random() * 2); // 2-3
    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return;

      p.position.set(
        position.x + (Math.random() - 0.5) * 0.15,
        position.y + 0.4 + Math.random() * 0.2,
        position.z + (Math.random() - 0.5) * 0.15
      );
      p.velocity.set(
        (Math.random() - 0.5) * 0.1,
        0.4 + Math.random() * 0.3, // gentle upward drift
        (Math.random() - 0.5) * 0.1
      );
      // Orange-yellow flame color
      const r = 1.0;
      const g = 0.5 + Math.random() * 0.4; // 0.5-0.9
      const b = 0.05 + Math.random() * 0.1;
      p.color.setRGB(r, g, b);
      p.life = 0.5 + Math.random() * 0.5; // 0.5-1.0s
      p.maxLife = p.life;
      p.size = 0.05;
      p.type = 'point';
      p.gravity = 0;
      p.fadeOut = true;
      p.opacity = 1;
      p.target = null;
    }
  }

  /**
   * Spawn portal particles. Purple sparkles that float upward slowly.
   * 1-2 particles per call, purple color (#7B2FBE), lifetime 1-2s.
   * @param {THREE.Vector3} position - portal block position
   */
  spawnPortalParticle(position) {
    const count = 1 + Math.floor(Math.random() * 2); // 1-2
    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return;

      p.position.set(
        position.x + (Math.random() - 0.5) * 0.8,
        position.y + Math.random() * 1.0,
        position.z + (Math.random() - 0.5) * 0.8
      );
      p.velocity.set(
        (Math.random() - 0.5) * 0.15,
        0.2 + Math.random() * 0.2, // slow upward float
        (Math.random() - 0.5) * 0.15
      );
      // Purple color (#7B2FBE -> r=0.482, g=0.184, b=0.745)
      p.color.setRGB(
        0.48 + (Math.random() - 0.5) * 0.1,
        0.18 + (Math.random() - 0.5) * 0.1,
        0.74 + (Math.random() - 0.5) * 0.1
      );
      p.life = 1.0 + Math.random() * 1.0; // 1-2s
      p.maxLife = p.life;
      p.size = 0.06;
      p.type = 'point';
      p.gravity = 0;
      p.fadeOut = true;
      p.opacity = 1;
      p.target = null;
    }
  }

  /**
   * Spawn portal particles with spiraling upward motion.
   * Call each frame with low spawn rate for a continuous effect.
   * 3-5 purple/magenta particles spiral around the portal center.
   * Colors: #8B00FF, #6A0DAD, #9932CC.
   * @param {THREE.Vector3} portalCenter - center of the portal
   */
  spawnPortalParticles(portalCenter) {
    const count = 3 + Math.floor(Math.random() * 3); // 3-5
    const portalColors = [
      new THREE.Color(0x8B00FF),
      new THREE.Color(0x6A0DAD),
      new THREE.Color(0x9932CC),
    ];

    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return;

      // Start at a random angle around the portal center
      const angle = Math.random() * Math.PI * 2;
      const radius = 0.3 + Math.random() * 0.4;
      p.position.set(
        portalCenter.x + Math.cos(angle) * radius,
        portalCenter.y + Math.random() * 0.5,
        portalCenter.z + Math.sin(angle) * radius
      );

      // Spiral upward: tangential velocity + upward component
      const tangentSpeed = 0.8 + Math.random() * 0.6;
      p.velocity.set(
        -Math.sin(angle) * tangentSpeed + (Math.random() - 0.5) * 0.2,
        0.6 + Math.random() * 0.4,
        Math.cos(angle) * tangentSpeed + (Math.random() - 0.5) * 0.2
      );

      // Pick a random purple color from the palette
      const baseColor = portalColors[Math.floor(Math.random() * portalColors.length)];
      p.color.copy(baseColor);
      // Slight variation
      p.color.r += (Math.random() - 0.5) * 0.05;
      p.color.g += (Math.random() - 0.5) * 0.03;
      p.color.b += (Math.random() - 0.5) * 0.05;

      p.life = 0.8 + Math.random() * 0.7; // 0.8-1.5s
      p.maxLife = p.life;
      p.size = 0.05;
      p.type = 'point';
      p.gravity = 0;
      p.fadeOut = true;
      p.opacity = 1;
      p.target = null;
    }
  }

  /**
   * Spawn a single torch particle rising from torch position with slight drift.
   * Use timer-based spawning externally (call once every ~0.3 seconds).
   * Colors: #FF6600, #FFAA00 (orange/yellow).
   * @param {THREE.Vector3} torchPosition - top of the torch
   */
  spawnTorchParticle(torchPosition) {
    const p = this._acquire();
    if (!p) return;

    p.position.set(
      torchPosition.x + (Math.random() - 0.5) * 0.1,
      torchPosition.y + 0.45,
      torchPosition.z + (Math.random() - 0.5) * 0.1
    );

    // Rise upward with slight random horizontal drift
    p.velocity.set(
      (Math.random() - 0.5) * 0.15,
      0.5 + Math.random() * 0.3,
      (Math.random() - 0.5) * 0.15
    );

    // Orange/yellow color palette
    if (Math.random() > 0.5) {
      // #FF6600
      p.color.setRGB(1.0, 0.4 + Math.random() * 0.1, 0.0);
    } else {
      // #FFAA00
      p.color.setRGB(1.0, 0.65 + Math.random() * 0.1, 0.0);
    }

    p.life = 0.4 + Math.random() * 0.3; // 0.4-0.7s
    p.maxLife = p.life;
    p.size = 0.04;
    p.type = 'point';
    p.gravity = 0;
    p.fadeOut = true;
    p.opacity = 1;
    p.target = null;
  }

  /**
   * Spawn water splash particles when player falls into water.
   * 10-15 blue-tinted particles bursting upward from water surface.
   * Colors: #2196F3, #4488CC.
   * @param {THREE.Vector3} position - water surface position
   */
  spawnWaterSplash(position) {
    const count = 10 + Math.floor(Math.random() * 6); // 10-15
    const splashColors = [
      new THREE.Color(0x2196F3),
      new THREE.Color(0x4488CC),
    ];

    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return;

      p.position.copy(position);
      // Spread across the water surface
      p.position.x += (Math.random() - 0.5) * 0.8;
      p.position.z += (Math.random() - 0.5) * 0.8;

      // Burst upward and outward
      const angle = Math.random() * Math.PI * 2;
      const hSpeed = 1.5 + Math.random() * 2;
      p.velocity.set(
        Math.cos(angle) * hSpeed,
        2.5 + Math.random() * 2, // strong upward burst
        Math.sin(angle) * hSpeed
      );

      // Pick blue color
      const baseColor = splashColors[Math.floor(Math.random() * splashColors.length)];
      p.color.copy(baseColor);
      // Slight brightness variation
      p.color.r += (Math.random() - 0.5) * 0.05;
      p.color.g += (Math.random() - 0.5) * 0.08;
      p.color.b += (Math.random() - 0.5) * 0.05;

      p.life = 0.5 + Math.random() * 0.4; // 0.5-0.9s
      p.maxLife = p.life;
      p.size = 0.06;
      p.type = 'cube';
      p.gravity = -12;
      p.fadeOut = true;
      p.opacity = 1;
      p.target = null;
    }
  }

  /**
   * Spawn a single musical note particle rising and fading.
   * Random color from a musical palette (green, blue, purple, red, yellow).
   * @param {THREE.Vector3} position - note block / jukebox position
   */
  spawnMusicalNote(position) {
    const p = this._acquire();
    if (!p) return;

    // Musical color palette
    const noteColors = [
      new THREE.Color(0x22CC22), // green
      new THREE.Color(0x2288FF), // blue
      new THREE.Color(0x9944CC), // purple
      new THREE.Color(0xDD3333), // red
      new THREE.Color(0xEECC22), // yellow
      new THREE.Color(0xFF66AA), // pink
      new THREE.Color(0x44DDDD), // cyan
    ];

    p.position.set(
      position.x + (Math.random() - 0.5) * 0.3,
      position.y + 0.8,
      position.z + (Math.random() - 0.5) * 0.3
    );

    // Float upward with gentle drift
    p.velocity.set(
      (Math.random() - 0.5) * 0.5,
      1.0 + Math.random() * 0.5,
      (Math.random() - 0.5) * 0.5
    );

    const noteColor = noteColors[Math.floor(Math.random() * noteColors.length)];
    p.color.copy(noteColor);

    p.life = 1.0 + Math.random() * 0.5; // 1.0-1.5s
    p.maxLife = p.life;
    p.size = 0.08;
    p.type = 'cube';
    p.gravity = 0;
    p.fadeOut = true;
    p.opacity = 1;
    p.target = null;
  }

  /**
   * Spawn smoke particles (call each frame for continuous effect).
   * Internally manages a timer; emits 1 particle every 0.5s.
   * @param {THREE.Vector3} position - smoke source position
   */
  spawnSmoke(position) {
    const key = `smoke_${Math.floor(position.x)},${Math.floor(position.y)},${Math.floor(position.z)}`;
    if (!this._smokeTimers.has(key)) {
      this._smokeTimers.set(key, 0);
    }
  }

  /**
   * Update smoke timers and spawn particles.
   * @param {number} dt
   * @param {THREE.Vector3[]} smokePositions - all nearby smoke source positions
   */
  updateSmoke(dt, smokePositions) {
    for (const pos of smokePositions) {
      const key = `smoke_${Math.floor(pos.x)},${Math.floor(pos.y)},${Math.floor(pos.z)}`;
      let timer = this._smokeTimers.get(key) || 0;
      timer += dt;

      if (timer >= 0.5) {
        timer = 0;
        const p = this._acquire();
        if (!p) return;

        p.position.set(
          pos.x + (Math.random() - 0.5) * 0.2,
          pos.y + 0.3,
          pos.z + (Math.random() - 0.5) * 0.2
        );
        p.velocity.set(
          (Math.random() - 0.5) * 0.1,
          0.3 + Math.random() * 0.3, // slow upward drift 0.3-0.6 blocks/sec
          (Math.random() - 0.5) * 0.1
        );
        // Gray
        const gray = 0.4 + Math.random() * 0.2;
        p.color.setRGB(gray, gray, gray);
        p.life = 1.5 + Math.random() * 1; // 1.5-2.5s
        p.maxLife = p.life;
        p.size = 0.08;
        p.type = 'point';
        p.gravity = 0;
        p.fadeOut = true;
        p.opacity = 1;
        p.target = null;
      }

      this._smokeTimers.set(key, timer);
    }

    // Clean up
    if (this._smokeTimers.size > 200) {
      this._smokeTimers.clear();
    }
  }

  /**
   * Spawn smoke poof when a mob dies.
   * 8-12 gray smoke particles expanding outward.
   * @param {THREE.Vector3} position - mob death position
   */
  spawnDeathSmoke(position) {
    const count = 8 + Math.floor(Math.random() * 5);
    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return;

      p.position.copy(position);
      p.position.y += 0.5 + Math.random() * 0.5;
      p.position.x += (Math.random() - 0.5) * 0.6;
      p.position.z += (Math.random() - 0.5) * 0.6;

      const angle = Math.random() * Math.PI * 2;
      const speed = 0.5 + Math.random() * 1.0;
      p.velocity.set(
        Math.cos(angle) * speed,
        0.8 + Math.random() * 0.6,
        Math.sin(angle) * speed
      );

      const gray = 0.6 + Math.random() * 0.2;
      p.color.setRGB(gray, gray, gray);
      p.life = 0.6 + Math.random() * 0.4;
      p.maxLife = p.life;
      p.size = 0.12;
      p.type = 'cube';
      p.gravity = -2;
      p.fadeOut = true;
      p.opacity = 1;
      p.target = null;
    }
  }

  /**
   * Spawn sparkle when player picks up an item.
   * 4-6 white/gold sparkles rising briefly.
   * @param {THREE.Vector3} position - pickup position
   */
  spawnPickupSparkle(position) {
    const count = 4 + Math.floor(Math.random() * 3);
    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return;

      p.position.copy(position);
      p.position.x += (Math.random() - 0.5) * 0.3;
      p.position.y += Math.random() * 0.3;
      p.position.z += (Math.random() - 0.5) * 0.3;

      p.velocity.set(
        (Math.random() - 0.5) * 0.5,
        1.0 + Math.random() * 0.5,
        (Math.random() - 0.5) * 0.5
      );

      // White-gold sparkle
      p.color.setRGB(1.0, 0.9 + Math.random() * 0.1, 0.6 + Math.random() * 0.2);
      p.life = 0.3 + Math.random() * 0.2;
      p.maxLife = p.life;
      p.size = 0.04;
      p.type = 'point';
      p.gravity = -3;
      p.fadeOut = true;
      p.opacity = 1;
      p.target = null;
    }
  }

  /**
   * Spawn dust trail when player runs on solid ground.
   * 1-2 small brown particles behind the player.
   * @param {THREE.Vector3} position - player feet position
   * @param {number} blockColor - hex color of the ground block
   */
  spawnRunDust(position, blockColor) {
    const count = 1 + Math.floor(Math.random() * 2);
    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return;

      p.position.set(
        position.x + (Math.random() - 0.5) * 0.4,
        position.y + 0.05,
        position.z + (Math.random() - 0.5) * 0.4
      );

      p.velocity.set(
        (Math.random() - 0.5) * 0.3,
        0.3 + Math.random() * 0.2,
        (Math.random() - 0.5) * 0.3
      );

      // Slightly varied brown/tan color based on block
      const r = ((blockColor >> 16) & 0xff) / 255;
      const g = ((blockColor >> 8) & 0xff) / 255;
      const b = (blockColor & 0xff) / 255;
      p.color.setRGB(
        r * 0.7 + Math.random() * 0.1,
        g * 0.7 + Math.random() * 0.1,
        b * 0.7 + Math.random() * 0.1
      );
      p.life = 0.3 + Math.random() * 0.2;
      p.maxLife = p.life;
      p.size = 0.04;
      p.type = 'point';
      p.gravity = -2;
      p.fadeOut = true;
      p.opacity = 0.6;
      p.target = null;
    }
  }

  /**
   * Level-up burst — spherical explosion of green + gold particles.
   */
  spawnLevelUpBurst(position) {
    const count = 40;
    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return;

      // Random direction on sphere
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const speed = 2 + Math.random() * 3;

      p.position.set(
        position.x,
        position.y + 0.9,
        position.z
      );
      p.velocity.set(
        Math.sin(phi) * Math.cos(theta) * speed,
        Math.sin(phi) * Math.sin(theta) * speed + 1,
        Math.cos(phi) * speed
      );

      // Alternate green and gold
      if (Math.random() > 0.4) {
        p.color.setRGB(0.5 + Math.random() * 0.3, 0.9 + Math.random() * 0.1, 0.1);
      } else {
        p.color.setRGB(1.0, 0.84 + Math.random() * 0.1, 0.0);
      }

      p.life = 1.0 + Math.random() * 0.8;
      p.maxLife = p.life;
      p.size = 0.08 + Math.random() * 0.04;
      p.type = 'cube';
      p.gravity = -3;
      p.fadeOut = true;
      p.opacity = 1.0;
      p.target = null;
    }
  }

  /**
   * Enchant sparkle — purple sparkles around enchanted items/enchanting table.
   */
  spawnEnchantSparkle(position) {
    for (let i = 0; i < 3; i++) {
      const p = this._acquire();
      if (!p) return;
      p.position.set(
        position.x + (Math.random() - 0.5) * 1.5,
        position.y + Math.random() * 2,
        position.z + (Math.random() - 0.5) * 1.5
      );
      p.velocity.set(
        (Math.random() - 0.5) * 0.5,
        0.5 + Math.random() * 0.5,
        (Math.random() - 0.5) * 0.5
      );
      p.color.setRGB(0.6 + Math.random() * 0.2, 0.2, 0.8 + Math.random() * 0.2);
      p.life = 0.8 + Math.random() * 0.5;
      p.maxLife = p.life;
      p.size = 0.04;
      p.type = 'point';
      p.gravity = -1;
      p.fadeOut = true;
      p.opacity = 1.0;
      p.target = null;
    }
  }

  /**
   * Potion splash — colored burst when potion effect is applied.
   */
  spawnPotionSplash(position, hexColor) {
    const count = 15;
    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return;
      const angle = Math.random() * Math.PI * 2;
      const speed = 1 + Math.random() * 2;
      p.position.set(position.x, position.y + 1, position.z);
      p.velocity.set(
        Math.cos(angle) * speed,
        1 + Math.random() * 2,
        Math.sin(angle) * speed
      );
      const r = ((hexColor >> 16) & 0xff) / 255;
      const g = ((hexColor >> 8) & 0xff) / 255;
      const b = (hexColor & 0xff) / 255;
      p.color.setRGB(r, g, b);
      p.life = 0.5 + Math.random() * 0.5;
      p.maxLife = p.life;
      p.size = 0.06;
      p.type = 'cube';
      p.gravity = -5;
      p.fadeOut = true;
      p.opacity = 0.9;
      p.target = null;
    }
  }

  /**
   * Heart particles — for breeding/taming.
   */
  spawnHearts(position) {
    for (let i = 0; i < 5; i++) {
      const p = this._acquire();
      if (!p) return;
      p.position.set(
        position.x + (Math.random() - 0.5) * 0.8,
        position.y + 1 + Math.random() * 0.5,
        position.z + (Math.random() - 0.5) * 0.8
      );
      p.velocity.set(
        (Math.random() - 0.5) * 0.3,
        0.8 + Math.random() * 0.5,
        (Math.random() - 0.5) * 0.3
      );
      p.color.setRGB(1.0, 0.2, 0.3);
      p.life = 1.0 + Math.random() * 0.5;
      p.maxLife = p.life;
      p.size = 0.1;
      p.type = 'cube';
      p.gravity = -1;
      p.fadeOut = true;
      p.opacity = 1.0;
      p.target = null;
    }
  }

  /**
   * Sprint dust — more dramatic dust cloud when sprinting.
   * 3-5 larger particles with wider spread.
   */
  spawnSprintDust(position, blockColor) {
    const count = 3 + Math.floor(Math.random() * 3);
    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      if (!p) return;

      p.position.set(
        position.x + (Math.random() - 0.5) * 0.6,
        position.y + 0.05,
        position.z + (Math.random() - 0.5) * 0.6
      );
      p.velocity.set(
        (Math.random() - 0.5) * 0.8,
        0.5 + Math.random() * 0.4,
        (Math.random() - 0.5) * 0.8
      );

      const r = ((blockColor >> 16) & 0xff) / 255;
      const g = ((blockColor >> 8) & 0xff) / 255;
      const b = (blockColor & 0xff) / 255;
      p.color.setRGB(
        r * 0.6 + Math.random() * 0.15,
        g * 0.6 + Math.random() * 0.15,
        b * 0.6 + Math.random() * 0.15
      );
      p.life = 0.5 + Math.random() * 0.3;
      p.maxLife = p.life;
      p.size = 0.07;
      p.type = 'cube';
      p.gravity = -1;
      p.fadeOut = true;
      p.opacity = 0.7;
      p.target = null;
    }
  }

  // ------------------------------------------------------------------
  // Internal: pool management
  // ------------------------------------------------------------------

  _createParticleObj() {
    return {
      position: new THREE.Vector3(),
      velocity: new THREE.Vector3(),
      color: new THREE.Color(1, 1, 1),
      life: 0,
      maxLife: 1,
      size: 0.1,
      type: 'cube',  // 'cube' or 'point'
      gravity: 0,
      fadeOut: false,
      opacity: 1,
      target: null,
    };
  }

  _acquire() {
    if (this._pool.length === 0) {
      // Pool exhausted; steal oldest active particle
      if (this._active.length > 0) {
        const stolen = this._active.shift();
        return stolen;
      }
      return null;
    }
    const p = this._pool.pop();
    this._active.push(p);
    return p;
  }

  _recycle(activeIndex) {
    const p = this._active[activeIndex];
    // Reset
    p.velocity.set(0, 0, 0);
    p.life = 0;
    p.target = null;
    // Move from active to pool
    this._active.splice(activeIndex, 1);
    this._pool.push(p);
  }

  // ------------------------------------------------------------------
  // Internal: rebuild InstancedMesh data
  // ------------------------------------------------------------------

  _rebuildInstances() {
    let cubeIdx = 0;
    let pointIdx = 0;

    for (const p of this._active) {
      const isCube = p.type === 'cube';
      const mesh = isCube ? this._cubeMesh : this._pointMesh;
      const idx = isCube ? cubeIdx++ : pointIdx++;

      if (idx >= MAX_PARTICLES) continue;

      _dummy.position.copy(p.position);
      const scale = p.size * (p.fadeOut ? Math.max(0.2, p.opacity) : 1);
      _dummy.scale.setScalar(scale / 0.1); // normalize to unit geo size
      _dummy.updateMatrix();
      mesh.setMatrixAt(idx, _dummy.matrix);

      // Color with opacity baked in (since instanced doesn't support per-instance opacity natively)
      _color.copy(p.color);
      if (p.fadeOut) {
        _color.multiplyScalar(p.opacity);
      }
      mesh.instanceColor.setXYZ(idx, _color.r, _color.g, _color.b);
    }

    this._cubeMesh.count = cubeIdx;
    this._pointMesh.count = pointIdx;

    if (cubeIdx > 0) {
      this._cubeMesh.instanceMatrix.needsUpdate = true;
      this._cubeMesh.instanceColor.needsUpdate = true;
    }
    if (pointIdx > 0) {
      this._pointMesh.instanceMatrix.needsUpdate = true;
      this._pointMesh.instanceColor.needsUpdate = true;
    }
  }
}
